/* eslint-disable lingui/no-unlocalized-strings */
import Handsontable from 'handsontable'
import { escape } from 'lodash'
import size from 'lodash/size'
import { decodeURI } from '@utils/link'
import { patternToHtml } from 'dataContainer/transformers/pattern.transformers'
import { UrlAudits } from 'handsontable/utils/codes.url-audits.js'
import { isLinkValid } from 'handsontable/utils/link.util'
import { renderErrorCorrectionTooltip } from './errorCorrection'
import { renderV2LinkValidation } from './link.renderers'
import { sanitizeHtml } from './sanitize'

const templateFieldsIds = new Map()

/**
 * HTML text instead of a React Component;
 * ideal to prevent needing REACTDOM.renderToString which can be a performance hit on the tables.
 * @param params
 * @param {boolean} params.isReadOnly Optional. Is the cell a part of read only column.
 * @param {boolean} params.isEmptyRow Optional. Toggle css class if field is empty.
 * @param {Array} params.chips An array of chip values.
 * @param {Array} params.invalidListValues Collection of values in the list that are invalid.
 * @returns {string} Does not return a React component.
 */
export function chipsToString({
  isReadonly = false,
  isEmptyRow = false,
  chips,
  invalidListValues = [],
}) {
  if (!chips?.length) return ''

  const autocompleteArrow = '<div className="htAutocompleteArrow">▼</div>'
  return `
    <div class="chips-wrapper list-chips ${isEmptyRow ? 'visible-on-active' : ''}">
      <div class="list-chips-items">
      ${chips
        ?.map(
          chip =>
            `<span class="${
              invalidListValues.includes(chip) ? '-invalid-value' : ''
            } list-chips-items__chip">${chip}</span>`,
        )
        .join('')}
      </div>
      ${isReadonly ? autocompleteArrow : ''}
    </div>
  `
}

const svgIconNewWindowHTML = `<svg xmlns="http://www.w3.org/2000/svg" height="18" viewBox="0 0 24 24" width="18"><path d="M0 0h24v24H0z" fill="none"/><path fill="#0091FF" d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z"/></svg>`

/**
 * Gets the renderer that is handles the read-oonly view of a cell.
 * @param {*} templateField
 * @param {*} dataContainer
 * @returns {function}
 */
export function getRenderer({ templateField, dataContainer, featureChipsUILists = false }) {
  /**
   * Responsible for rendering the state of the cell into HandsOnTable.
   * Rendering for the read-only view of a cell.
   * @param {*} hotInstance
   * @param {HTMLTableCellElement} td
   * @param {number} row The row index position.
   * @param {*} column
   * @param {*} prop
   * @param {string} value The cell value.
   */
  return function renderer(hotInstance, td, row, column, prop, value) {
    if (!dataContainer.data.length) {
      return
    }

    const rawValue = value

    // quick sanitization.
    if (value && typeof value === 'string') {
      value = escape(value)
    }

    if (!templateFieldsIds.has(column)) {
      templateFieldsIds.set(column, templateField.field_id)
    }

    const dataContainerRow = dataContainer?.data[row]
    const cvidExists = Object.values(dataContainerRow)?.find(v =>
      Object.prototype.hasOwnProperty.call(v, 'cvid'),
    )
    const validationErrors = dataContainerRow[templateField.field_id]
      ? dataContainerRow[templateField.field_id].metadata.validationErrors
      : []
    const warnings = dataContainerRow[templateField.field_id]
      ? dataContainerRow[templateField.field_id].metadata.warnings
      : []
    const comment = dataContainerRow?.[templateField.field_id]?.metadata?.comment || {}
    const isVariable = templateField.field.segment_type === 'variable'
    const governanceOwner = isVariable ? templateField : templateField.field
    let useTooltipBuilder = true
    const isEmptyRow = hotInstance.isEmptyRow(row)
    const governanceType = governanceOwner.governance_type
    const hasComment = size(comment) > 0
    const hasErrors = !!validationErrors?.length
    const hasWarnings = !!warnings?.length

    const errorCorrection = dataContainerRow?.[templateField.field_id]?.errorCorrection
    const hasErrorCorrection = !!errorCorrection?.modification
    const modificationReverted = hasErrorCorrection && !!errorCorrection?.modificationReverted
    const errorCorrected =
      !hasErrors &&
      !hasWarnings &&
      errorCorrection?.modification === rawValue &&
      !modificationReverted

    /*
    If the template field is a classification column and the value is not present,
    we need to set the value to an empty object so that the renderer can still render the cell.
    This is because the classification column is not editable and the value is not present in the dataContainerRow.
    * */
    const valueObj =
      templateField.is_classification_column && !dataContainerRow[templateField.field_id]
        ? {
            renderOnly: true,
            metadata: {
              readOnly: true,
            },
          }
        : dataContainerRow[templateField.field_id]

    switch (governanceType) {
      case 'cv_id': {
        valueObj.metadata.readOnly = true
        const dxCls = isEmptyRow ? 'visible-on-active' : ''
        value = !value ? `<div class="htDatePlaceholder ${dxCls}">[Claravine Id]</div>` : value
        break
      }
      case 'autofill': {
        valueObj.metadata.readOnly = true
        const afCls = isEmptyRow ? 'visible-on-active' : ''
        value = value ? `<div class="${afCls}">${value}</div>` : value
        break
      }
      case 'autonumber': {
        valueObj.metadata.readOnly = true
        const autoCls = isEmptyRow ? 'visible-on-active' : ''
        value = value
          ? `<div class="${autoCls}">${value}</div>`
          : `<div class="htDatePlaceholder ${autoCls}">[Auto Number]</div>`
        break
      }
      case 'random': {
        valueObj.metadata.readOnly = true
        const ranCls = isEmptyRow ? 'visible-on-active' : ''
        value = value
          ? `<div class="${ranCls}">${value}</div>`
          : `<div class="htDatePlaceholder ${ranCls}">[Random]</div>`
        break
      }
      case 'concatenation': {
        valueObj.metadata.readOnly = true
        const conCls = isEmptyRow ? 'visible-on-active' : ''
        value = value ? `<div class="${conCls}">${value}</div>` : value
        break
      }
      case 'constant': {
        valueObj.metadata.readOnly = true
        value = value || governanceOwner.constant_value
        const constCls = isEmptyRow ? 'visible-on-active' : ''
        value = `<div class="${constCls}">${value}</div>`
        break
      }
      case 'list': {
        var listGovernance = governanceOwner.list_governance || {}
        const { lists, list_column, delimiter } = listGovernance
        var list = lists || {}
        const isDependentList = list.type === 'dependent'
        const isDrillDownList = list.type === 'drill-down'
        const isNotFirstColumn = list_column && list_column.precedence > 0

        // Multi value is inherited from the first column of a drill-down list.
        // This replicates the behavior of setCellType applied to the Handsontable Binder through the Data Container.
        const firstListColumn = listGovernance?.meta?.allColumnsInList[0]
        const isFirstListColumnMulti = firstListColumn?.field?.list_governance?.multi
        const firstListColumnDelimiter = firstListColumn?.field?.list_governance?.delimiter
        const isMultiValue = isDrillDownList ? isFirstListColumnMulti : listGovernance?.multi
        const listDelimiter = isDrillDownList ? firstListColumnDelimiter : delimiter

        if (isDependentList && isNotFirstColumn) {
          valueObj.metadata.readOnly = true
          break
        }

        // Chips component UI
        if (featureChipsUILists && isMultiValue) {
          let chips = []
          const isValueStringWithDelimiter = typeof value === 'string' && value && listDelimiter
          const isValueEmpty = value === ''

          if (isValueStringWithDelimiter) {
            chips = value.split(listDelimiter)
          } else if (isValueEmpty) {
            chips = []
          } else if (value) {
            chips = [value]
          }

          value = chipsToString({ isReadOnly: valueObj.metadata.readOnly, isEmptyRow, chips })

          if (chips && chips?.length) {
            Handsontable.hooks.run(hotInstance, 'setRowHeightObserver')
          }

          break
        }

        // Plain text list value
        if (!valueObj.metadata.readOnly) {
          const visibleCls = isEmptyRow ? 'visible-on-active' : ''
          value = `<div class="${visibleCls}">${
            value || ''
          }<div class="htAutocompleteArrow">▼</div></div>`
        } else {
          const visibleCls = isEmptyRow ? 'visible-on-active' : ''
          value = `<div class="${visibleCls}">${value || ''}</div>`
        }

        break
      }
      case 'text':
        break
      case 'link': {
        const constantClass = isEmptyRow ? 'visible-on-active' : ''

        value = value
          ? `<div data-cell-renderer-type="link" class="${constantClass}"><a href="${value}" target="_blank">${svgIconNewWindowHTML}</a> ${decodeURI(
              value,
            )}</div>`
          : ''
        break
      }
      case 'link-validation': {
        // Link Status field
        useTooltipBuilder = false
        const linkValue = hotInstance.getDataAtRowProp(row, `${governanceOwner.id}.cell_value`)
        if (isEmptyRow) {
          value = '<div></div>'
          renderToTd(td, value)
        } else {
          const linkGovernance =
            dataContainer.getValueHolderForRowAndColumn(row, {
              field_id: governanceOwner.id,
            }).metadata?.link_governance ||
            Object.values(dataContainer.allRenderColumns).find(rc => {
              return rc.governance_type === 'link-validation'
            })
          renderLinkValidation(value, linkGovernance, linkValue)
        }
        break
      }
      case 'date': {
        if (governanceOwner.date.auto_populate) {
          valueObj.metadata.readOnly = true
          const dtCls = isEmptyRow ? 'visible-on-active' : ''
          value = !value ? `<div class="htDatePlaceholder ${dtCls}">[Submission Date]</div>` : value
        }
        break
      }
      case 'shortened link': {
        valueObj.metadata.readOnly = true
        if (value === 'Bitlink creation limit reached') {
          value =
            '<a href="https://app.bitly.com/organization/1/detail" target="_blank">Bitlink creation limit reached</a>'
        } else if (value) {
          value = `<a href="${value}" target="_blank">${value}</a>`
        }
        break
      }
      case 'qr-code': {
        valueObj.metadata.readOnly = true
        if (value) {
          value = `<a href="${value}" target="_blank">${value}</a>`
        }
        break
      }
      default:
        valueObj.metadata.readOnly = true
        break
    }

    if (templateField.dependsOnSibling) {
      valueObj.metadata.readOnly = true
    }

    if (templateField.field.is_pattern) {
      valueObj.metadata.readOnly = true
      value = renderPattern(value)
      td.classList.add('htPattern')
    }

    if (
      templateField.renderOnly ||
      valueObj.metadata.readOnly ||
      templateField.governance_type === 'link-validation'
    ) {
      td.classList.add('htDimmed')
    }

    td.setAttribute('data-row-col', `${row}-${column}`)
    td.setAttribute('data-test-page-id', `${row}-${column}`)
    td.setAttribute('data-test-row-idx', row)
    td.setAttribute('data-test-col-idx', column)
    td.setAttribute('data-nth-row-type', (row + 1) % 2 === 0 ? 'even' : 'odd')

    // Do not reset innerHtml on link-validation, since it replaces validation status callback.
    const allowInnerHtmlValueReset = () => governanceType !== 'link-validation'

    // Return BEFORE warning styles and tooltips are applied; prevent validation warnings on empty rows.
    // Return AFTER determing classes, readOnly, et cetera to have a chance to affect empty field styling.
    // Do not validate empty rows; rows beyond the length of the dataset (accounting for inserted new rows).
    if (row > getLastIndexOfDataRange()) {
      if (allowInnerHtmlValueReset()) {
        renderToTd(td, value)
      }

      // When outisde the dataset range, we still need to know if it's a "new" empty row.
      if (!newRowWithData()) {
        return td
      }
    }

    // If the CVID key does not exist,
    // and it's within the range of data rows (due to inserting a new row) —
    // then we set the value to the cell and let the warnings and tooltips follow. If we do not set this value
    // then adding a new row causes the previous value to show in the new row cell.
    if (!cvidExists && allowInnerHtmlValueReset()) {
      renderToTd(td, value)
    }

    if (templateField.governance_type === 'date') {
      // the "date" case of the switch statement doesn't work in all
      // cases.  This catches the case of dates that are part of
      // patterns.  Not variables that are governed as dates.

      // only show the dropdown arrow if not read only
      if (!valueObj.metadata.readOnly) {
        const visibleCls = isEmptyRow ? 'visible-on-active' : ''
        value = `${value || ''}<div class="htAutocompleteArrow ${visibleCls}">▼</div>`
      }
    }

    renderWarningStyles(td, value)

    if (useTooltipBuilder) {
      displayTooltip(td, value)
    }

    addBottomBorderAboveCurrent({ data: dataContainer?.data, row, column })
    removeRightBorder({ data: dataContainer?.data, row, column, td })

    // add validation errors to the cell metadata so that we can grab it later
    // this is not ideal... the errors should be calculated outside of the renderer.
    // but since we don't have time to refactor it right now, we'll just go with it.

    return td

    /**
     * Get the last row index of all data, including new rows, to determine what
     * rows are in the data range to be validated; skip validating empty rows.
     * - Cannot rely on dataset length, need to account for new rows added.
     * @returns {number} Row index of the last item in the data.
     */
    function getLastIndexOfDataRange() {
      let lastItemIndex = dataContainer?.data?.length
      const data = dataContainer?.data.slice()

      for (let i = data.length - 1; i >= 0; i--) {
        const cvidExists = Object.values(data[i]).find(v =>
          Object.prototype.hasOwnProperty.call(v, 'cvid'),
        )
        lastItemIndex = i

        if (cvidExists) {
          break // stop loop
        }
      }

      return lastItemIndex
    }

    /**
     * Identify a new row that has content. Based on if the cell_value is present, and
     * if the cell is visible, not readonly, and has a row number value.
     * - Useful for identifying the new row when it's outside the dataset range.
     * @returns {boolean}
     */
    function newRowWithData() {
      const match = Object.values(dataContainerRow)?.find(v => {
        if (v.cell_value && v.metadata?.isVisible && !v.metadata?.readOnly && v.metadata?.row)
          return true

        return false
      })

      if (match) return true
      return false
    }

    function renderWarningStyles(td) {
      if (hasWarnings) {
        td.classList.add('htWarning')
      }

      if (hasErrors || hasComment) {
        td.classList.add('htError')
      }

      if (errorCorrected) {
        td.classList.add('htSuccess')
      }
    }

    function displayTooltip(td, value) {
      if (hasErrorCorrection) {
        renderErrorCorrectionTooltip(
          td,
          value,
          errorCorrection,
          toggleErrorCorrection,
          dataContainer?.data?.length,
        )
      } else if (hasErrors || hasWarnings || hasComment) {
        renderToTd(td, getValidationHtml() + (value || ''))
      } else {
        renderToTd(td, value)
      }

      function getValidationHtml() {
        const isLastColumn =
          td?.cellIndex === td?.parentNode?.childElementCount - 1 && td?.cellIndex !== 1

        let validationHtml = `<div class="tooltipWrapper ${
          isLastColumn ? 'validationTooltipMargin' : ''
        }"><div class="validationTooltip"><h3>Warning</h3><ul>`
        validationErrors.forEach(addMessageToValidationHtml)
        warnings.forEach(addMessageToValidationHtml)
        hasComment && addMessageToValidationHtml(comment.message)
        validationHtml += '</ul></div></div>'

        return validationHtml

        function addMessageToValidationHtml(message) {
          const validationListItem = `<li>${message}</li>`
          validationHtml += validationListItem
        }
      }

      function toggleErrorCorrection() {
        // If it is currently reverted then the next value should be the modification (corrected value)
        const cellData = modificationReverted
          ? errorCorrection.modification
          : errorCorrection.previousValue
        dataContainer.recordChange(row, templateField.field_id, cellData, value)

        // Update the error correction state and re-render
        if (dataContainer.data[row] && dataContainer.data[row][templateField.field_id]) {
          dataContainer.data[row][templateField.field_id].errorCorrection = {
            ...errorCorrection,
            modificationReverted: !modificationReverted,
          }
        }
        dataContainer.changeCallback()
      }
    }

    function renderLinkValidation(value, linkGovernance, linkValue) {
      // Note: value contains the url audits stored in the database, constructed from tables `link_governance`, `dataset_value_url_audit` and `url_audit`
      // Note: linkValue is the current value in the Link field (should be url), if it is changed, it might not match the link status stored in the database
      clearTd()
      td.classList.add('htDimmed')
      if (!linkValue || !isLinkValid(linkValue, linkGovernance)) {
        // Link is blank or invalid, link status should be blank
        return
      }

      const urlAudits = UrlAudits.find(linkValue)
      renderV2LinkValidation(td, urlAudits, linkValue, linkGovernance, row)

      function clearTd() {
        td.innerText = ''
      }
    }

    function renderPattern(value) {
      if (value) {
        return value
      }
      return patternToHtml(templateField, hotInstance, row)
    }
  }

  /**
   * This function is needed to eliminate overlapping borders between cells.
   *
   * If we just add a top border to the current cell with errors/warnings,
   * the top border overlaps with the bottom border of the cell directly above.
   *
   * This function checks if the current cell has errors/warnings, then updates the
   * bottom-border-color of the cell directly above.
   * @param params
   * @param {object} params.data Table data object
   * @param {number} params.row Row index
   * @param {column} params.column Column index
   * @returns
   */
  function addBottomBorderAboveCurrent({ data, row, column }) {
    if (row === 0) return

    const currentRow = data?.[row]
    const fieldId = templateFieldsIds.get(column)
    const cellAboveCurrent = data?.[row - 1]?.[fieldId]

    const warningsInCellAbove = cellAboveCurrent?.metadata?.warnings
    const validationErrorsInCellAbove = cellAboveCurrent?.metadata?.validationErrors

    const warningsInCurrentCell = currentRow?.[fieldId]?.metadata?.warnings
    const validationErrorsInCurrentCell = currentRow?.[fieldId]?.metadata?.validationErrors

    const shouldAddBottomBorderToCellAbove =
      (warningsInCellAbove?.length === 0 && warningsInCurrentCell?.length > 0) ||
      (validationErrorsInCellAbove?.length === 0 && validationErrorsInCurrentCell?.length > 0)

    if (shouldAddBottomBorderToCellAbove) {
      const tdAbove = document.querySelector(`[data-row-col="${row - 1}-${column}"]`)

      if (!tdAbove) return

      if (warningsInCellAbove?.length > 0) {
        tdAbove.classList.add('add-bottom-border-warning')
      } else {
        tdAbove.classList.add('add-bottom-border-error')
      }
    }
  }

  function removeRightBorder({ data, row, column, td }) {
    const currentRow = data?.[row]
    const fieldId = templateFieldsIds.get(column)
    const fieldIdOfCellToRight = templateFieldsIds.get(column + 1)
    const cellRightOfCurrent = data?.[row]?.[fieldIdOfCellToRight]

    const warningsInCellToRight = cellRightOfCurrent?.metadata?.warnings
    const validationErrorsInCellToRight = cellRightOfCurrent?.metadata?.validationErrors

    const warningsInCurrentCell = currentRow?.[fieldId]?.metadata?.warnings
    const validationErrorsInCurrentCell = currentRow?.[fieldId]?.metadata?.validationErrors

    const shouldRemoveRightBorder =
      (warningsInCellToRight?.length > 0 && warningsInCurrentCell?.length > 0) ||
      (validationErrorsInCellToRight?.length > 0 && validationErrorsInCurrentCell?.length > 0)

    if (shouldRemoveRightBorder) {
      if (warningsInCellToRight?.length > 0) {
        td.classList.add('remove-right-border-warning')
      } else {
        td.classList.add('remove-right-border-error')
      }
    }
  }
}

function renderToTd(td, htmlString) {
  const sanitized = sanitizeHtml(htmlString)
  td.innerHTML = sanitized
}
